import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, vibrate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);
  const tracked = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          vibrate([100, 50, 100]);
          if (!tracked.current) {
            tracked.current = true;
            trackEvent("purchase", {
              value: (res.data.amount || 0) / 100,
              currency: (res.data.currency || "eur").toUpperCase(),
              plan: res.data.plan,
            });
          }
          setStatus("paid");
          checkAuth();
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {}
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, checkAuth]);

  return (
    <div className="grain min-h-screen bg-[#09090b] text-zinc-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full border-2 border-pink-500 border-t-transparent animate-spin mb-6" />
            <p className="text-zinc-400">Validation du paiement…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400 mb-6" size={56} />
            <h1 className="font-display text-3xl font-black">Bienvenue en Premium 🎉</h1>
            <p className="text-zinc-400 mt-3">Analyses illimitées débloquées. Le Doc est à toi.</p>
            <button
              data-testid="success-continue"
              onClick={() => navigate("/app")}
              className="mt-8 w-full rounded-full bg-pink-500 text-white font-bold py-4 hover:bg-pink-600 transition-colors glow-pink"
            >
              Commencer
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="mx-auto text-red-400 mb-6" size={56} />
            <h1 className="font-display text-2xl font-black">Paiement non confirmé</h1>
            <p className="text-zinc-400 mt-3">
              {status === "timeout" ? "La confirmation prend plus de temps que prévu." : "Un souci est survenu."}
            </p>
            <button
              onClick={() => navigate("/pricing")}
              className="mt-8 w-full rounded-full bg-zinc-800 text-white font-bold py-4 hover:bg-zinc-700 transition-colors"
            >
              Réessayer
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="grain min-h-screen bg-[#09090b] text-zinc-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <XCircle className="mx-auto text-zinc-500 mb-6" size={56} />
        <h1 className="font-display text-2xl font-black">Paiement annulé</h1>
        <p className="text-zinc-400 mt-3">Pas de souci, tu peux revenir quand tu veux.</p>
        <button
          data-testid="cancel-back"
          onClick={() => navigate("/pricing")}
          className="mt-8 w-full rounded-full bg-pink-500 text-white font-bold py-4 hover:bg-pink-600 transition-colors"
        >
          Revoir les offres
        </button>
      </div>
    </div>
  );
}
